import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { Close, Refresh } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationApiService, MotzuMetafieldField, SpecCatalogEntry, StoreMetafieldDefinitionOption } from '../services/integrationApi';
import { tokens } from '../theme/tokens';
import { StatusChip } from './ui';
import { useAppTranslation } from '../i18n/useAppTranslation';
import {
  metafieldTypeLabel,
  motzuKeySet,
  previewSpecWrite,
  suggestSpecLabel,
  visibleStoreDefinitions,
} from './mapping/mappingModel';
import { SpecPickerPopover } from './mapping/SpecPickerPopover';

/** Store metafield | spec | preview | remove; on a phone the spec and preview wrap under the metafield. */
const DEFINITION_TABLE_COLUMNS = { xs: 'minmax(0,1fr) 36px', md: '1fr 1.15fr 0.8fr 36px' };
const FULL_ROW_ON_PHONE = (order: number) => ({ gridColumn: { xs: '1 / -1', md: 'auto' }, order: { xs: order, md: 0 } });

export type SpecMetafieldRow = {
  specLabel: string;
  namespace: string;
  key: string;
};

interface ShopifySpecMetafieldsSectionProps {
  integrationId: string;
  rows: SpecMetafieldRow[];
  onChange: (rows: SpecMetafieldRow[]) => void;
  onOpenProductFields?: () => void;
}

const cardSx = {
  bgcolor: tokens.surface.card,
  border: '1px solid',
  borderColor: tokens.line.default,
  borderRadius: `${tokens.radius.panel}px`,
  boxShadow: tokens.shadow.card,
} as const;

const definitionId = (row: { namespace: string; key: string }) => `${row.namespace}.${row.key}`;

/**
 * Fills metafields the store already defined. Rows start from those
 * definitions. Motzu's own custom.* fields are left out — those are created
 * on the Product Fields tab.
 */
const ShopifySpecMetafieldsSection: React.FC<ShopifySpecMetafieldsSectionProps> = ({
  integrationId,
  rows,
  onChange,
  onOpenProductFields,
}) => {
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  const optionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', integrationId],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(integrationId),
    staleTime: 5 * 60 * 1000,
  });
  // The spec catalog is cached 60 s on the server. The query key is shared with the mapping panels,
  // whose query functions never pass `refresh`, so an invalidate could refetch through one of them and
  // keep the server cache; fetch the key here with the refresh flag and write the result to the shared cache.
  const [rereading, setRereading] = useState(false);
  const reread = async () => {
    setRereading(true);
    try {
      await queryClient.fetchQuery({
        queryKey: ['shopify-spec-metafield-options', integrationId],
        queryFn: () => integrationApiService.getSpecMetafieldOptions(integrationId, { refresh: true }),
        staleTime: 0,
      });
    } catch {
      // The query surface shows the error state; nothing else to do here.
    } finally {
      setRereading(false);
    }
  };
  const fieldsQuery = useQuery({
    queryKey: ['motzu-metafield-fields'],
    queryFn: () => integrationApiService.getMotzuMetafieldFields(),
    staleTime: Infinity,
  });

  const motzuKeys = useMemo(() => motzuKeySet(fieldsQuery.data || []), [fieldsQuery.data]);
  const definitions = useMemo(
    () => visibleStoreDefinitions(optionsQuery.data?.definitions || [], motzuKeys),
    [optionsQuery.data, motzuKeys],
  );
  const catalog = useMemo<SpecCatalogEntry[]>(() => {
    const fromApi = optionsQuery.data?.specCatalog;
    if (fromApi?.length) return fromApi;
    return (optionsQuery.data?.specLabels || []).map((label) => ({
      label,
      productCount: 0,
      sample: '',
      kind: 'text' as const,
    }));
  }, [optionsQuery.data]);
  const hasCounts = Boolean(optionsQuery.data?.specCatalog?.length);

  // A picked spec that is one of Motzu's standard fields carries its definition;
  // anything else came in through vendor data.
  // Matched by the canonical spec path the API carries (spec "Mount" is Motzu's
  // "Lens mount"), never by label; the name is only a fallback for an API that
  // predates spec paths.
  const motzuFieldByPath = useMemo(() => {
    const map = new Map<string, MotzuMetafieldField>();
    for (const field of fieldsQuery.data || []) if (field.specPath) map.set(field.specPath, field);
    return map;
  }, [fieldsQuery.data]);
  const motzuFieldByLabel = useMemo(() => {
    const map = new Map<string, MotzuMetafieldField>();
    for (const field of fieldsQuery.data || []) map.set(field.name.trim().toLowerCase(), field);
    return map;
  }, [fieldsQuery.data]);

  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      if (row.specLabel.trim()) map.set(definitionId(row), row.specLabel);
    }
    return map;
  }, [rows]);

  const suggestions = useMemo(() => {
    const next = new Map<string, string>();
    for (const definition of definitions) {
      const id = definitionId(definition);
      if (labelById.get(id) || !definition.writable) continue;
      const label = suggestSpecLabel(definition, catalog);
      if (label) next.set(id, label);
    }
    return next;
  }, [definitions, labelById, catalog]);

  const filled = definitions.filter((definition) => Boolean(labelById.get(definitionId(definition)))).length;

  const writeRows = (nextLabels: Map<string, string>) => {
    const visibleIds = new Set(definitions.map(definitionId));
    const kept = rows.filter((row) => {
      const id = definitionId(row);
      return !visibleIds.has(id) && !motzuKeys.has(id);
    });
    const mapped: SpecMetafieldRow[] = [];
    for (const definition of definitions) {
      const label = nextLabels.get(definitionId(definition))?.trim();
      if (!label) continue;
      mapped.push({ specLabel: label, namespace: definition.namespace, key: definition.key });
    }
    onChange([...mapped, ...kept]);
  };

  const setLabel = (definition: StoreMetafieldDefinitionOption, label: string) => {
    const next = new Map(labelById);
    if (label.trim()) next.set(definitionId(definition), label.trim());
    else next.delete(definitionId(definition));
    writeRows(next);
  };

  const acceptAll = () => {
    const next = new Map(labelById);
    suggestions.forEach((label, id) => next.set(id, label));
    writeRows(next);
    setSuggestionsDismissed(true);
  };

  const openDefinition = definitions.find((definition) => definitionId(definition) === openId) || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ ...cardSx, px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>Spec → metafield</Typography>
            <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, maxWidth: 720, mt: 0.5 }}>
              Fill metafields your store already defined with a Motzu catalog spec. For example, Lens mount → custom.mount gives a Sony a7 IV “Sony E”, so it shows up under your Mount filter.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={optionsQuery.isFetching || rereading ? <CircularProgress size={14} /> : <Refresh />}
            onClick={reread}
            sx={{ height: 36, alignSelf: 'flex-start', borderColor: tokens.line.default, borderRadius: '9px', fontWeight: 600, color: tokens.ink.body }}
          >
            Re-read definitions
          </Button>
        </Box>
        <Box sx={{ px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: tokens.surface.sunken, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 13, color: tokens.ink.body }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{filled} of {definitions.length} store metafields filled.</Box>
            {' '}Only definitions under Settings → Custom data → Products are listed; the sync never creates new ones.
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>
            Motzu&apos;s own fields live in{' '}
            {onOpenProductFields ? (
              <Button onClick={onOpenProductFields} sx={{ p: 0, minWidth: 0, fontWeight: 700, verticalAlign: 'baseline' }}>
                Product Fields
              </Button>
            ) : (
              'Product Fields'
            )}
            .
          </Typography>
        </Box>
      </Box>

      {(optionsQuery.isError || optionsQuery.data?.definitionsError) && (
        <Alert severity="warning">
          {optionsQuery.data?.definitionsError || 'Could not load the store’s metafield definitions.'}
        </Alert>
      )}

      {suggestions.size > 0 && !suggestionsDismissed && (
        <Box sx={{ ...cardSx, bgcolor: tokens.accent.tint, borderColor: tokens.accent.border, px: 2.25, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13.5, flex: '1 1 240px' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{suggestions.size} metafields have a spec with a matching name.</Box>
            {' '}Check the dashed suggestions below.
          </Typography>
          <Button size="small" variant="contained" onClick={acceptAll} sx={{ fontWeight: 700 }}>Accept all</Button>
        </Box>
      )}

      <Box sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: DEFINITION_TABLE_COLUMNS, px: 2, height: 34, alignItems: 'center', bgcolor: tokens.surface.sunken }}>
          {['Store metafield', 'Filled from Motzu spec', 'Preview'].map((label) => (
            <Typography key={label} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
              {label}
            </Typography>
          ))}
          <span />
        </Box>
        {optionsQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        )}
        {!optionsQuery.isLoading && definitions.length === 0 && (
          <Typography sx={{ px: 2, py: 2, fontSize: 13.5, color: tokens.ink.muted }}>
            No store metafield definitions yet. Create them in Shopify under Settings → Custom data → Products, then re-read definitions.
          </Typography>
        )}
        {definitions.map((definition) => {
          const id = definitionId(definition);
          const label = labelById.get(id) || '';
          const suggestion = !label ? suggestions.get(id) : undefined;
          const entry = catalog.find((item) => item.label === label);
          const preview = entry ? previewSpecWrite(entry.sample, definition.type) : null;
          const motzuField = label
            ? (entry?.specPath ? motzuFieldByPath.get(entry.specPath) : undefined) ||
              motzuFieldByLabel.get(label.trim().toLowerCase())
            : undefined;
          const isCustomSpec = Boolean(label) && (!motzuField || motzuField.tier === 'custom');
          return (
            <Box
              key={id}
              sx={{
                display: 'grid',
                gridTemplateColumns: DEFINITION_TABLE_COLUMNS,
                gap: 1,
                alignItems: 'center',
                px: 2,
                py: 1.25,
                borderTop: '1px solid',
                borderColor: tokens.line.hairline,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{definition.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                  <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.muted }} noWrap>
                    {definition.namespace}.{definition.key}
                  </Typography>
                  <Box sx={{ px: 0.6, py: 0.1, borderRadius: '5px', bgcolor: tokens.surface.tint, fontSize: 10.5, fontWeight: 600, color: tokens.ink.muted }}>
                    {metafieldTypeLabel(definition.type)}
                  </Box>
                </Box>
              </Box>
              <Box sx={FULL_ROW_ON_PHONE(1)}>
                <Button
                  onClick={(event) => {
                    setAnchor(event.currentTarget);
                    setOpenId(id);
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    height: 36,
                    px: 1.25,
                    width: '100%',
                    border: '1px solid',
                    borderColor: tokens.line.default,
                    borderRadius: '8px',
                    color: label ? tokens.ink.primary : tokens.ink.faint,
                    fontWeight: label ? 600 : 500,
                    fontSize: 13,
                  }}
                >
                  {label || 'Not filled'}
                </Button>
                {label && (
                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <StatusChip
                      label={t(isCustomSpec ? 'integrations.specTier.custom' : 'integrations.specTier.canonical')}
                      tone={isCustomSpec ? 'caution' : 'neutral'}
                    />
                    <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
                      {motzuField && motzuField.tier !== 'custom'
                        ? motzuField.description
                        : t(motzuField ? 'integrations.specTier.customHint' : 'integrations.specTier.vendorSpecHint')}
                    </Typography>
                  </Box>
                )}
                {suggestion && (
                  <Button
                    size="small"
                    onClick={() => setLabel(definition, suggestion)}
                    sx={{
                      mt: 0.5,
                      height: 24,
                      border: '1px dashed',
                      borderColor: tokens.accent.border,
                      bgcolor: tokens.accent.tint,
                      color: tokens.accent.hover,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {suggestion}
                  </Button>
                )}
              </Box>
              <Box sx={FULL_ROW_ON_PHONE(2)}>
                {label && preview ? (
                  <>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: tokens.font.mono }}>{preview.value}</Typography>
                    {preview.note && (
                      <Typography sx={{ fontSize: 11.5, color: preview.lossy ? tokens.signal.caution.ink : tokens.ink.muted }}>
                        {preview.note}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.faint }}>Left as is in Shopify</Typography>
                )}
                {!definition.writable && label && (
                  <Typography sx={{ fontSize: 11.5, color: tokens.signal.caution.ink }}>Sync cannot fill this type</Typography>
                )}
              </Box>
              <IconButton
                size="small"
                aria-label={`Stop filling ${definition.name}`}
                disabled={!label}
                onClick={() => setLabel(definition, '')}
              >
                <Close sx={{ fontSize: 16, color: label ? tokens.ink.muted : tokens.ink.disabled }} />
              </IconButton>
            </Box>
          );
        })}
        <Typography sx={{ px: 2, py: 1.25, fontSize: 12, color: tokens.ink.muted, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
          Metafield not listed? Create it in Shopify under Settings → Custom data → Products, then re-read definitions.
        </Typography>
      </Box>

      <SpecPickerPopover
        open={Boolean(openDefinition)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setOpenId(null);
        }}
        catalog={catalog}
        hasCounts={hasCounts}
        definitionType={openDefinition?.type ?? null}
        currentLabel={openDefinition ? labelById.get(definitionId(openDefinition)) : undefined}
        syncedProducts={optionsQuery.data?.syncedProducts}
        onPick={(label) => {
          if (openDefinition) setLabel(openDefinition, label);
          setAnchor(null);
          setOpenId(null);
        }}
      />
    </Box>
  );
};

export default ShopifySpecMetafieldsSection;
